/**
 * CPAYMENT-1742: одностраничный экран оплаты — перевод и чек на одной странице.
 *
 * Экран не склеивает два прежних экрана подряд, а раскладывает их в одну последовательность из
 * двух пронумерованных шагов: «переведите» → «приложите чек». Нумерация здесь несёт смысл (это
 * настоящий порядок действий), поэтому иерархия строится на ней, а не на цветных плашках:
 *
 *  - шаг 1 отдаёт реквизит и сумму одним привычным блоком с копированием, под ним банк и
 *    инструкция; инструкция короткая — всё, что дальше, уже нарисовано на самом экране;
 *  - шаг 2 — спокойный блок загрузки вместо аварийной жёлтой карточки: требование чека сказано
 *    один раз в подзаголовке, а не криком;
 *  - внизу привычная пара кнопок «Вернуться» / «Я оплатил».
 *
 * Если чек не требуется (`simpleConfirmation !== false`), второй шаг не рисуется вовсе — вместе с
 * ним уходит и нумерация первого: остаётся обычный экран реквизитов, где «Я оплатил» подтверждает
 * оплату сразу.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Transaction } from '../../../services/types';
import { getAzPhoneNumberMask, getCardNumberMask, getPhoneNumberMask } from '../../../services/utils';
import m10Logo from '../../../assets/images/m10.svg';
import { track, buildBasePayload } from '../../../analytics';
import QR from '../../../components/atoms/QR/QR';
import QrPayBlock from '../../../components/organisms/QrPayBlock/QrPayBlock';
import useIsDesktop from '../../../hooks/useIsDesktop';
import { useReceiptPicker } from '../../../hooks/useReceiptPicker';
import {
  getReceiptInstructionsKey,
} from '../../../services/receiptUploadPolicy';
import UploadNotice from '../../../components/UploadNotice/UploadNotice';
import {
  uploadErrorTextKey,
  uploadReceipt as sendReceipt,
} from '../../../services/receiptUpload';
import {
  OnePageScreen,
  OnePageLeaveModal,
  OnePageLowTimeModal,
  OnePageSpinnerRing,
  OnePageUploadBtn,
  LION_GREEN,
  LION_GREEN_LIGHT,
  LION_GREY,
} from '../components/OnePageLayout';
import OnePageAmountAlert from '../components/OnePageAmountAlert';
import { showsAmountChangeNotice } from '../../../services/amountChange';
import { BankLogo } from '../../../utils/bankLogos';
import { copyToClipboard } from '../../../services/clipboard';
import { CopiedField } from '../../../analytics/events';
import type { CancelReasonOption, DeclineContext } from '../../../services/types';

// ─── Icons ────────────────────────────────────────────────────────────────────
const SbpIcon = () => (
  <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 22, width: 'auto', flexShrink: 0 }}>
    <path d="M0 10.8838L6.05499 21.7067V28.3084L0.00708333 39.11L0 10.8838Z" fill="#5B57A2"/>
    <path d="M23.2487 17.7676L28.9224 14.2901L40.5341 14.2793L23.2487 24.8685V17.7676Z" fill="#D90751"/>
    <path d="M23.2166 10.8196L23.2487 25.1487L17.1795 21.4196V0L23.217 10.8196H23.2166Z" fill="#FAB718"/>
    <path d="M40.5341 14.2796L28.922 14.2904L23.2166 10.8196L17.1795 0L40.5337 14.2796H40.5341Z" fill="#ED6F26"/>
    <path d="M23.2487 39.17V32.2179L17.1795 28.5596L17.1829 50.0004L23.2487 39.17Z" fill="#63B22F"/>
    <path d="M28.9083 35.7242L6.05458 21.7067L0 10.8838L40.5095 35.71L28.9079 35.7242H28.9083Z" fill="#1487C9"/>
    <path d="M17.1834 50.0004L23.2484 39.17L28.908 35.7241L40.5092 35.71L17.1834 50.0004Z" fill="#017F36"/>
    <path d="M0.0071106 39.1093L17.2292 28.5593L11.4392 25.0068L6.05502 28.3077L0.0071106 39.1093Z" fill="#984995"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M66.2021 26.6991C70.1096 26.6991 73.0112 24.3033 73.0112 20.6713C73.0112 17.1557 70.8705 14.8734 67.2924 14.8734C65.6412 14.8734 64.2787 15.4544 63.2525 16.4574C63.4974 14.3847 65.2504 12.871 67.1803 12.871C67.6259 12.871 70.9791 12.8641 70.9791 12.8641L72.8755 9.23535C72.8755 9.23535 68.6655 9.33138 66.7083 9.33138C62.2367 9.4091 59.2162 13.4736 59.2162 18.4101C59.2162 24.1617 62.162 26.6991 66.2021 26.6991ZM66.2259 18.0875C67.6767 18.0875 68.6824 19.0408 68.6824 20.6709C68.6824 22.1378 67.7888 23.3467 66.2259 23.3499C64.7308 23.3499 63.7249 22.2297 63.7249 20.6957C63.7249 19.0653 64.7308 18.0875 66.2259 18.0875ZM88.0995 14.038V26.2621H83.7344V17.6871H79.5316V26.2621H75.1665V14.0372L88.0995 14.038ZM53.0928 22.8763C54.6325 22.764 55.6632 22.1695 55.6632 22.1695L57.2786 25.1582C56.244 25.8227 54.5466 26.2548 53.3162 26.3004C48.9141 26.3813 45.852 23.7027 45.852 19.8001C45.852 15.9468 49.1098 13.2043 53.3162 13.2043C54.9828 13.2043 56.4157 13.6514 57.4916 14.4669C57.2651 14.8681 56.9573 15.4239 56.6648 15.9557L56.1962 16.8094L55.77 17.589C55.77 17.589 54.2199 16.7768 53.0482 16.7768C51.1374 16.7768 49.7458 17.8873 49.7458 19.8245C49.7458 21.811 51.3228 22.9288 53.0928 22.8763Z" fill="black"/>
  </svg>
);

const CardIcon = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="1" y="1" width="22" height="16" rx="3" stroke="#333" strokeWidth="1.5" fill="none"/>
    <rect x="1" y="5" width="22" height="3" fill="#333"/>
    <rect x="4" y="11" width="6" height="2" rx="1" fill="#999"/>
  </svg>
);

const QrIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="14" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="2" y="14" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="5" y="5" width="2" height="2" fill="#333"/>
    <rect x="17" y="5" width="2" height="2" fill="#333"/>
    <rect x="5" y="17" width="2" height="2" fill="#333"/>
    <rect x="14" y="14" width="3" height="3" fill="#333"/>
    <rect x="19" y="19" width="3" height="3" fill="#333"/>
  </svg>
);

const CopyIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6654 8.6026V11.4026C10.6654 13.7359 9.73203 14.6693 7.3987 14.6693H4.5987C2.26536 14.6693 1.33203 13.7359 1.33203 11.4026V8.6026C1.33203 6.26927 2.26536 5.33594 4.5987 5.33594H7.3987C9.73203 5.33594 10.6654 6.26927 10.6654 8.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.6654 4.6026V7.4026C14.6654 9.73594 13.732 10.6693 11.3987 10.6693H10.6654V8.6026C10.6654 6.26927 9.73203 5.33594 7.3987 5.33594H5.33203V4.6026C5.33203 2.26927 6.26536 1.33594 8.5987 1.33594H11.3987C13.732 1.33594 14.6654 2.26927 14.6654 4.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CopyDoneSvg = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10l4 4 8-8" stroke={LION_GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M15.75 8.25v8.25a3.75 3.75 0 1 1-7.5 0V6.75a2.25 2.25 0 1 1 4.5 0v9a.75.75 0 0 1-1.5 0V8.25"
      stroke={LION_GREEN} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RemoveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.66 17.66L6.34 6.34" stroke="#8a8a8a" strokeWidth="2.6" strokeLinecap="round"/>
    <path d="M6.34 17.66L17.66 6.34" stroke="#8a8a8a" strokeWidth="2.6" strokeLinecap="round"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="6" y="2" width="12" height="20" rx="3" stroke={LION_GREEN} strokeWidth="1.6"/>
    <path d="M10.5 18.5h3" stroke={LION_GREEN} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

// Фирменный знак m10 — такой же цветной логотип бренда, как знак СБП рядом: клиент узнаёт
// приложение, в котором будет платить, раньше, чем читает заголовок.
const M10Icon = () => (
  <img src={m10Logo} alt='m10' style={{ height: 22, width: 22, flexShrink: 0 }} />
);

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Блок реквизитов: реквизит и сумма одним блоком ───────────────────────────
const RequisitesBlock = styled.div`
  background-color: ${LION_GREEN_LIGHT};
  border-radius: 20px;
  padding: 4px;
  text-align: left;
`;

const CopyRowInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;

  & + & {
    border-top: 1px solid rgba(106, 170, 58, 0.15);
  }
`;

const CopyText = styled.div`
  width: 100%;
  min-width: 0;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: black;
  text-align: left;
  word-break: break-all;
`;

const CopyLabel = styled.div`
  font-size: 11px;
  color: ${LION_GREY};
  font-family: 'PT Mono', monospace;
`;

// ─── Секции-шаги ──────────────────────────────────────────────────────────────
const Section = styled.section`
  text-align: left;
  margin-top: 22px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`;

const SectionNum = styled.span<{ $done?: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $done }) => ($done ? '#fff' : '#5c8a1e')};
  background: ${({ $done }) => ($done ? LION_GREEN : 'rgb(212, 239, 175)')};
  transition: background 0.2s, color 0.2s;
`;

const SectionTitle = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  line-height: 1.35;
`;

const SectionHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: #555;
  line-height: 1.45;
  margin-top: 4px;
`;

const SectionBody = styled.div<{ $numbered?: boolean }>`
  padding-left: ${({ $numbered }) => ($numbered ? '32px' : '0')};

  @media (max-width: 420px) {
    padding-left: 0;
  }
`;

// Тип перевода без нумерации: когда шаг всего один, он и есть весь экран.
const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 12px;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: #333;
  text-align: left;
`;

// ─── Копирование ──────────────────────────────────────────────────────────────
const CopyBtn = styled.button<{ $copied: boolean }>`
  background: rgba(106, 170, 58, 0.12);
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${({ $copied }) => ($copied ? 1 : 0.75)};
  transition: opacity 0.2s, box-shadow 0.2s, transform 0.1s;
  &:hover {
    opacity: 1;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
  }
  &:active { transform: scale(0.9); }
`;

const BankLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 0 4px;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: #555;

  img {
    height: 18px;
    width: auto;
    display: block;
  }
`;

const BankSpacer = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

/**
 * Единственная тревожная плашка на экране. Она здесь потому, что цена ошибки необратима: номер
 * выдан под один депозит, и второй перевод на него деньгами клиента уже не станет.
 */
const WarnNote = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #f0b8a8;
  background: #fff4f0;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #b3341f;
  text-align: left;
`;

// Инструкция: тонкая линия вместо коробки — подсказка, а не второй по важности блок.
const Steps = styled.ol`
  margin: 12px 0 0;
  padding: 2px 0 2px 18px;
  border-left: 2px solid rgba(106, 170, 58, 0.25);
  list-style: none;
  counter-reset: hint;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: #6b6b6b;
  line-height: 1.75;

  li {
    counter-increment: hint;
  }

  li::before {
    content: counter(hint) '. ';
    color: ${LION_GREY};
  }
`;

// ─── Загрузка чека ────────────────────────────────────────────────────────────
// Дропзоны здесь нет намеренно: на платёжной странице клиент чаще всего с телефона, а рамка
// «перетащите файл» обещала бы способ, которым он всё равно не воспользуется.
const UploadHint = styled.div`
  /* The caption sits right under a full-width tap target; 8px read as part of the button. */
  margin-top: 12px;
  font-family: 'PT Mono', monospace;
  font-size: 11px;
  color: ${LION_GREY};
  line-height: 1.5;
  text-align: center;
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  background: ${LION_GREEN_LIGHT};
  border: 1px solid rgba(106, 170, 58, 0.35);
  animation: ${fadeIn} 0.2s ease;
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: 'PT Mono', monospace;
  font-size: 13px;
  color: #2f6b18;
  word-break: break-all;
  text-align: left;
`;

const IconBtn = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:disabled { cursor: not-allowed; opacity: 0.4; }
`;

const PhoneToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px auto 0;
  padding: 4px 2px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: ${LION_GREEN};
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const QrPanel = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 10px;
  padding: 12px;
  border-radius: 16px;
  background: #f7f7f7;
  animation: ${fadeIn} 0.2s ease;
`;

const QrFrame = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-shrink: 0;
`;

const QrText = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 11px;
  color: ${LION_GREY};
  line-height: 1.5;
  text-align: left;
`;

// ─── Завершение ───────────────────────────────────────────────────────────────
const FooterBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  margin-bottom: 6px;
  @media (max-width: 575px) {
    flex-direction: column-reverse;
  }
`;

const BackBtn = styled.button`
  color: rgb(13, 110, 253);
  background-color: transparent;
  border: 1.5px solid rgb(13, 110, 253);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 400;
  font-family: PT Sans, sans-serif;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background-color: rgba(13, 110, 253, 0.06); }
`;

const PaidBtn = styled.button`
  background-color: #93c84a;
  color: #fff;
  border: 1.5px solid transparent;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  font-size: 14px;
  font-weight: 400;
  font-family: PT Sans, sans-serif;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:enabled { opacity: 0.85; }
  &:disabled { cursor: not-allowed; opacity: 0.6; box-shadow: none; }
`;

const PaidHint = styled.div`
  margin-bottom: 10px;
  font-family: 'PT Mono', monospace;
  font-size: 11px;
  color: ${LION_GREY};
  text-align: center;
`;

const Percent = styled.span`
  font-family: 'PT Mono', monospace;
  font-size: 13px;
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onCancel: (reason?: CancelReasonOption) => void;
  /** Cancel as a refusal of what this screen offers. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  /** Cancel survey items, in order; empty — no survey. */
  cancelReasons?: CancelReasonOption[];
  /** CPAYMENT-1730: уход к мерчанту без отмены заявки. */
  onLeave?: () => void;
  /** CPAYMENT-1730: клиент уже отметил, что платит — выход спрашивает, что именно он хочет. */
  receiptIntent?: boolean;
  /** CPAYMENT-1731: до конца окна осталось меньше 2 минут и его ещё можно продлить. */
  lowTimeWarning?: boolean;
  onExtendTime?: () => void;
  onDismissLowTime?: () => void;
  /** Финальное подтверждение оплаты: чек уже на бэкенде (или не требуется). */
  onConfirm: () => Promise<void>;
}

const OnePagePaymentPage = ({
  transaction,
  uuid,
  timeLeft,
  onCancel,
  onDeclineOffer,
  cancelReasons,
  onLeave,
  receiptIntent,
  lowTimeWarning,
  onExtendTime,
  onDismissLowTime,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const { requisite } = transaction;
  const isDesktop = useIsDesktop();
  const [showLeave, setShowLeave] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [errorText, setErrorText] = useState('');
  // CPAYMENT-1786: a retry in progress is not an error — the client is told to wait, not to act.
  const [retryNotice, setRetryNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [amountCopied, setAmountCopied] = useState(false);
  const [requisiteCopied, setRequisiteCopied] = useState(false);

  // CPAYMENT-1822: picking a file — the press, the cancel, the empty pick and the silence — lives in
  // one hook shared by every screen that owns its input.
  const picker = useReceiptPicker({
    uuid,
    transaction,
    onFile: setFile,
    onError: (key) => setErrorText(t(key)),
    onBusy: setSending,
    onPickStart: () => {
      setErrorText('');
      setRetryNotice('');
    },
  });

  // CPAYMENT-1637: у QR-реквизита нет номера для копирования — только ссылка
  // в paymentUrl, по ней рисуем код и кнопку перехода.
  const isQrRequisite = requisite.type === 'qr_code' && !!requisite.paymentUrl;
  const isCard = !!requisite.cardNumber && !requisite.phone;
  // m10 — кошелёк, а не СБП: номер адресует конкретный счёт в приложении, выдан под один депозит
  // и живёт по азербайджанскому формату. Отсюда свой заголовок, свой знак, своя инструкция и
  // предупреждение, которого у остальных телефонных методов нет.
  const isM10 = transaction.paymentMethod === 'M10AZN';
  // Требование чека решает бэкенд — страница только рисует (или не рисует) второй шаг.
  const receiptRequired = transaction.simpleConfirmation === false;
  const canConfirm = !sending && (!receiptRequired || !!file);

  const amountText = `${transaction.amount.toLocaleString('ru-RU')} ${requisite.currency}`;
  const requisiteValue = isCard
    ? getCardNumberMask(requisite.cardNumber || '') || requisite.cardNumber || ''
    : isM10
      ? getAzPhoneNumberMask(requisite.phone || '') || requisite.phone || ''
      : getPhoneNumberMask(requisite.phone || '') || requisite.phone || '';
  const methodIcon = isQrRequisite ? <QrIcon /> : isCard ? <CardIcon /> : isM10 ? <M10Icon /> : <SbpIcon />;
  const methodLabel = isQrRequisite
    ? t('qr.title')
    : isCard
      ? t('sbp.transferByCard')
      : isM10
        ? t('m10.transferByPhone')
        : t('sbp.transferByPhone');
  const requisiteCopyValue = (isCard ? requisite.cardNumber : requisite.phone) || '';

  const method = transaction.paymentMethod?.toUpperCase() ?? '';
  const bankLabel = method.includes('SBER')
    ? 'Сбер'
    : method.includes('ALFA')
      ? 'Альфа'
      : method.includes('TBANK') || method.includes('TINKOFF')
        ? 'Т-Банк'
        : method.includes('OZON')
          ? 'Озон'
          : method.includes('VTB')
            ? 'ВТБ'
            : requisite.acquiringBank || '';

  // Поле по умолчанию — реквизит: это единственная строка, ради которой существует копирование.
  // Сумма передаёт своё явно.
  const copy = (
    value: string,
    done: (v: boolean) => void,
    field: CopiedField = 'requisite',
  ) => {
    copyToClipboard(value.replace(/\s/g, ''), field).then((ok) => {
      if (!ok) return;
      done(true);
      setTimeout(() => done(false), 1500);
    });
  };

  // CPAYMENT-1786: sending, classifying, retrying and reporting all live in services/receiptUpload.
  // This screen only decides what the client sees.
  const uploadReceipt = async (): Promise<boolean> => {
    if (!file) return false;
    setUploadProgress(0);
    setRetryNotice('');

    const result = await sendReceipt(uuid, transaction, file, {
      onProgress: setUploadProgress,
      onRetryScheduled: () => setRetryNotice(t('uploadStatus.retrying')),
    });
    setRetryNotice('');

    if (result.ok) return true;

    setUploadProgress(0);
    setErrorText(t(uploadErrorTextKey(result.reason, result.httpStatus)));
    return false;
  };

  /**
   * «Я оплатил» — единственное завершающее действие экрана: сначала чек уходит на бэкенд, затем
   * подтверждается оплата. Подтвердить оплату без обязательного чека нельзя — тикет уехал бы
   * оператору с пустыми руками.
   */
  const handlePaidClick = async () => {
    if (!canConfirm) return;
    // CPAYMENT-1786: tracked here, before the upload. `payment.confirm_clicked` fires inside
    // handleConfirm, which runs only once the receipt is already stored — so every click that
    // failed to upload was missing from the funnel entirely.
    track({ name: 'payment.paid_clicked', payload: buildBasePayload(uuid, transaction) });
    setSending(true);
    setErrorText('');
    try {
      if (receiptRequired) {
        const uploaded = await uploadReceipt();
        if (!uploaded) return;
      }
      await onConfirm();
    } finally {
      setSending(false);
    }
  };

  return (
    <OnePageScreen
      step={1}
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          {receiptRequired && !file && !sending && (
            <PaidHint>{t('sbp.instructionStep5')}</PaidHint>
          )}

          <FooterBtnRow>
            <BackBtn data-sentry-element='onepage-back' onClick={() => setShowLeave(true)}>{t('common.back')}</BackBtn>
            <PaidBtn data-sentry-element='onepage-paid' onClick={handlePaidClick} disabled={!canConfirm}>
              {sending ? (
                <>
                  <OnePageSpinnerRing size={20}>
                    <span style={{ fontSize: 7, color: '#fff' }}>●</span>
                  </OnePageSpinnerRing>
                  {uploadProgress > 0 && <Percent>{uploadProgress}%</Percent>}
                </>
              ) : (
                t('sbp.iPaid')
              )}
            </PaidBtn>
          </FooterBtnRow>

          {showLeave && (
            <OnePageLeaveModal
              onLeave={onCancel}
              onCancel={() => setShowLeave(false)}
              reasons={cancelReasons}
              receiptIntent={receiptIntent}
              onLeaveWithoutCancel={onLeave}
            />
          )}
          {/* CPAYMENT-1731: не наслаиваем предупреждение на уже открытый диалог выхода. */}
          {lowTimeWarning && !showLeave && onExtendTime && onDismissLowTime && (
            <OnePageLowTimeModal onExtend={onExtendTime} onDismiss={onDismissLowTime} />
          )}
        </>
      }
    >
      {/* ─── Шаг 1: перевод по реквизиту ──────────────────────────────────────
          Номер и заголовок шага появляются, только когда шагов действительно два: без чека
          «1. Оплата по реквизитам» обещало бы продолжение, которого не будет, да и полоса шагов
          сверху уже говорит то же самое. Остаётся привычная строка с типом перевода. */}
      <Section>
        {receiptRequired ? (
          <SectionHead>
            <SectionNum>1</SectionNum>
            <div>
              <SectionTitle>{t('steps.payByRequisites')}</SectionTitle>
              <SectionHint>
                {methodIcon}
                <span>{methodLabel}</span>
              </SectionHint>
            </div>
          </SectionHead>
        ) : (
          <TypeBadge>
            {methodIcon}
            <span>{methodLabel}</span>
          </TypeBadge>
        )}

        <SectionBody $numbered={receiptRequired}>
          <RequisitesBlock>
            {isQrRequisite ? (
              <QrPayBlock
                url={requisite.paymentUrl!}
                hint={t('qr.hint')}
                action={t('qr.action')}
              />
            ) : (
              <CopyRowInner>
                <CopyText>
                  <CopyLabel>{isCard ? t('sbp.copyCard') : t('sbp.copyPhone')}</CopyLabel>
                  {requisiteValue}
                </CopyText>
                <CopyBtn
                  $copied={requisiteCopied}
                  onClick={() => copy(requisiteCopyValue, setRequisiteCopied)}
                  aria-label={t('common.copy')}
                >
                  {requisiteCopied ? <CopyDoneSvg /> : <CopyIconSvg />}
                </CopyBtn>
              </CopyRowInner>
            )}

            <CopyRowInner>
              <CopyText>
                <CopyLabel>{t('sbp.copyAmount')}</CopyLabel>
                {amountText}
              </CopyText>
              <CopyBtn
                $copied={amountCopied}
                data-sentry-element='onepage-copy-amount'
                onClick={() => copy(`${transaction.amount}`, setAmountCopied, 'amount')}
                aria-label={t('common.copy')}
              >
                {amountCopied ? <CopyDoneSvg /> : <CopyIconSvg />}
              </CopyBtn>
            </CopyRowInner>
          </RequisitesBlock>

          {showsAmountChangeNotice(transaction) && (
            <OnePageAmountAlert transaction={transaction} onDecline={() => onDeclineOffer('amount_change')} />
          )}

          {bankLabel && (
            <BankLine>
              <span>{t('sbp.selectBank')}:</span>
              <strong>{bankLabel}</strong>
              <BankSpacer>
                <BankLogo code={requisite.acquiringBank} />
              </BankSpacer>
            </BankLine>
          )}

          {isM10 && <WarnNote>{t('m10.warning')}</WarnNote>}

          {/* Инструкция короткая: про чек говорит шаг 2, про подтверждение — кнопка внизу.
              Без чека шага 2 нет, поэтому финальное действие проговариваем строкой. */}
          <Steps>
            {isM10 ? (
              <>
                <li>{t('m10.instructionStep1')}</li>
                <li>{t('m10.instructionStep2')}</li>
                <li>{t('m10.instructionStep3')}</li>
              </>
            ) : (
              <>
                {/* Копировать у QR нечего — шаг про реквизиты пропускаем. */}
                {!isQrRequisite && <li>{t('sbp.instructionStep1')}</li>}
                <li>{t('sbp.instructionStep2')}</li>
                <li>{t('sbp.instructionStep3')}</li>
              </>
            )}
            {!receiptRequired && !isM10 && <li>{t('sbp.instructionStep4')}</li>}
          </Steps>
        </SectionBody>
      </Section>

      {/* ─── Шаг 2: чек ───────────────────────────────────────────────────── */}
      {receiptRequired && (
        <Section>
          <SectionHead>
            <SectionNum $done={!!file}>2</SectionNum>
            <div>
              <SectionTitle>{t('steps.uploadReceipt')}</SectionTitle>
              <SectionHint>{t('upload.requiredSub')}</SectionHint>
            </div>
          </SectionHead>

          <SectionBody $numbered>
            {file ? (
              <FileChip>
                <ClipIcon />
                <FileName>{file.name}</FileName>
                <IconBtn
                  disabled={sending}
                  onClick={() => {
                    if (sending) return;
                    setFile(null);
                  }}
                  data-sentry-element='onepage-file-remove'
                  aria-label={t('modal.cancel')}
                >
                  <RemoveIcon />
                </IconBtn>
              </FileChip>
            ) : (
              <>
                <OnePageUploadBtn
                  label={t('upload.uploadBtn')}
                  onClick={picker.open}
                  dataSentryElement='onepage-upload-pick'
                />
                <UploadHint>{t(getReceiptInstructionsKey(transaction.merchant))}</UploadHint>
                {/* CPAYMENT-1822: the dialog gave nothing back twice in a row. Whether it failed to
                    open or the client left it empty-handed, repeating the same tap is what he does
                    next — and it is what the lost deposits look like in the replays. */}
                {picker.helpVisible && <UploadNotice tone='neutral'>{t('upload.pickerHelp')}</UploadNotice>}
              </>
            )}

            {errorText && <UploadNotice tone='error'>{errorText}</UploadNotice>}
            {/* A retry in flight is neutral, not red — the client has nothing to fix and nothing to
                do except wait, and a red block here reads as "it failed again". */}
            {retryNotice && !errorText && <UploadNotice tone='neutral'>{retryNotice}</UploadNotice>}

            {/* CPAYMENT-1718: чек чаще всего лежит в телефоне. Код прячем под ссылку — на экране
                и без него хватает вещей, требующих внимания. */}
            {isDesktop && !file && (
              <>
                <PhoneToggle type='button' data-sentry-element='onepage-open-on-phone' onClick={() => setShowQr((v) => !v)}>
                  <PhoneIcon />
                  {t('upload.openOnPhone')}
                </PhoneToggle>
                {showQr && (
                  <QrPanel>
                    <QrFrame>
                      <QR data={window.location.href} size={96} bgColor='#ffffff' fgColor='#111111' />
                    </QrFrame>
                    <QrText>{t('upload.openOnPhoneHint')}</QrText>
                  </QrPanel>
                )}
              </>
            )}
          </SectionBody>
        </Section>
      )}
    </OnePageScreen>
  );
};

export default OnePagePaymentPage;
